// Shared types used by server, CLI, and Web UI

import type { Org } from "./constants.js";
import type { DevhubAssist, DevhubDraft, DevhubFeedback, DevhubSectionOwner, DevhubOrder, DevhubRedirect, DevhubRedirectSettings, DevhubAssetSummary, DevhubSdkSummary, DevhubTag, DevhubBatch } from "./devhub-types.js";
// Re-export the full DevHub type surface (now defined in devhub-types.ts) so
// existing `from "./types.js"` importers keep working.
export * from "./devhub-types.js";
// The corpus layout — pure path builders + the target type, shared by the API (which
// resolves the target from env) and the web UI (which must never infer it).
export * from "./devhub-corpus.js";

export enum AgentStatus {
  Online = "online",
  Offline = "offline",
}

// A human connected to the hub via the Web UI. Presence is heartbeat-based
// (the browser pings while the tab is open); a reaper flips it offline after
// silence — mirroring agent liveness. Keyed by the authenticated identity (OIDC
// sub, or "dev" under the local bypass).
export interface HumanPresence {
  id: string;
  name: string;
  email: string;
  /** Profile picture URL, if the IdP provides one. */
  image: string | null;
  /** Job title / position, if available. */
  position: string | null;
  status: AgentStatus;
  lastSeenAt: string;
}

export interface Agent {
  id: string;
  name: string;
  // The agentic-tool account driving this agent — its operator's name or email
  // (e.g. a git user.email, or the Claude Code / Codex account). Captured at
  // registration so the hub can list who is behind each agent. null if unknown.
  account: string | null;
  // The org this agent operates in (incode | metamap | aid), derived from its
  // working repo at registration. Every hub agent belongs to an org and is run
  // by a human operator (`account`) — generic, org-less agents are not allowed.
  org: string | null;
  // The repository this agent is working in (its working-dir repo name, e.g.
  // "incode-web-sdk"), reported by the CLI at registration. Lets the hub route
  // repo-scoped work (e.g. DevHub page assists) to the agent for that repo.
  // Mutable: refreshed on each re-register. null if unknown/unset.
  repo: string | null;
  // The LLM model driving this agent (e.g. "claude-opus-4-8", "claude-sonnet-4-6",
  // "gpt-5-codex"), reported by the CLI at registration. null if unknown/unset.
  // Mutable: updated on each re-register so it reflects the agent's current model.
  model: string | null;
  // The verified identity (Okta token subject) that registered this agent. Agent
  // ingress calls must come from the same subject — the body-supplied agentId is
  // not itself an identity. null when registered off-Okta or on rows from before
  // this field existed (those are adopted by the next authenticated re-register).
  owner: string | null;
  status: AgentStatus;
  connectedAt: string;
  lastSeenAt: string;
}

/**
 * A minted agent-ingress credential — the internal alternative to an Okta token
 * for agents that have no human behind them (the harness). Registration returns
 * the secret exactly once; only its SHA-256 is stored, keyed by that hash, so the
 * table can never leak a usable credential and lookup stays a single indexed read.
 *
 * Deliberately NOT a field on `Agent`: `/api/agents` serves agent documents to the
 * UI (unredacted to admins, spread-copied for everyone else), so anything living
 * on that document is a field away from being published.
 */
export interface AgentToken {
  /** SHA-256 of the presented secret, hex. The table's primary key. */
  tokenHash: string;
  /** The agent this credential acts as. */
  agentId: string;
  /**
   * A NON-SECRET leading fragment of the plaintext, e.g. "oha_7f3c9a1b" — enough
   * to tell two credentials apart in an admin view without the view holding one.
   * The remaining ~35 base64url characters are what make the token unguessable,
   * so this leaks nothing usable.
   *
   * Nullable because credentials minted before this field existed have none; a
   * reader must render "unknown", never a truncated hash.
   */
  tokenPrefix: string | null;
  createdAt: string;
  /** Refreshed on use, throttled — the signal for "is this agent still alive". */
  lastUsedAt: string | null;
  /** Set when superseded by a re-registration or revoked by an admin. */
  revokedAt: string | null;
}

export enum ApprovalStatus {
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected",
}

export interface SummaryProposal {
  id: string;
  sessionId: string;
  proposedBy: string; // agent id
  proposedAt: string;
  content: string;
  status: ApprovalStatus;
  resolvedAt?: string;
}

// What produced an output:
//  • Agent   — an agent's response
//  • Human   — a note typed by a human in the Web UI
//  • Context — a session-context document was pasted/updated (see Session.context)
export enum OutputKind {
  Agent = "agent",
  Human = "human",
  Context = "context",
}

export interface Output {
  id: string;
  sessionId: string;
  senderId: string;    // agent id, "human", or "context"
  senderName: string;
  content: string;
  createdAt: string;
  kind: OutputKind;
  isHumanNote: boolean; // retained for back-compat; true when kind === "human"
}

// A human associated with a session — its creator or an explicitly tagged user.
// Identity is the OIDC sub (the same key as Agent.owner and a human Output's
// senderId); email/name are for display and for the tag picker. Sessions are
// grouped by creator in the Web UI, and — for non-admins — a session is visible
// only to its creator and tagged users (see core/authz.ts isSessionParticipant).
export interface SessionUser {
  sub: string;
  email: string;
  name: string | null;
}

export interface Session {
  id: string;
  name: string;
  agentIds: string[]; // two or more participating agents
  createdAt: string;
  // Who created the session — the signed-in human, stamped at creation from the
  // verified Okta identity (or the local sign-in off-Okta). null on rows created
  // before this field existed; those are visible to admins only under the gate.
  createdBy: SessionUser | null;
  // Users explicitly tagged into the session (a collaboration affordance). They
  // see it under "Shared with me" and — for non-admins — it counts toward
  // visibility (creator OR tagged). Distinct from agentIds (the agent members).
  taggedUsers: SessionUser[];
  // Link sharing. When true, ANY signed-in user who has the session's URL may
  // open it — read the chat and post notes — without being a creator or tagged
  // participant. It widens READ (and note posting) only; managing the session
  // (agents, rename, delete, tags, context, approvals, and toggling this flag)
  // stays creator/tagged/admin-only. Absent/false ⇒ private (the default). See
  // core/authz.ts canViewSession / guardSessionView.
  shared?: boolean;
  // Per-agent read receipts: agentId → ISO timestamp of the most recent message
  // that agent has read (via read-inputs). Drives the "seen" indicator.
  seenBy?: Record<string, string>;
  summary: string;
  // Free-form markdown the human pastes in to seed the session. Agents pick it
  // up via read-inputs; the first to read it is expected to fold it into the
  // shared summary (see AGENT_USAGE.md §"Session context").
  context: string;
  contextUpdatedAt: string | null;
  pendingProposal: SummaryProposal | null;
  outputs: Output[];
}

// ── Tasks ───────────────────────────────────────────────────────────────────
// A task is an agent work item — a different grouping from a Session (which is
// human↔agent communication). A task targets one agent (one repo) or several
// (multiple repos). Created with no agents, it is "unassigned" and the
// orchestrator agent (omni-hub-agent) analyzes it and attaches the
// agents needed to finish it. An assigned agent picks the task up, writes a plan
// under .agents/plans/, registers that plan here, and notifies its operator.
// Status maps 1:1 to the board columns. "Needs assignment" is NOT a status — it
// is derived from `assignedAgentIds.length === 0` (a queued task may or may not
// have agents attached yet).
export enum TaskStatus {
  Queue = "queue",            // created / waiting to be worked (assigned, or awaiting the orchestrator)
  InProgress = "in_progress", // an agent has picked it up and is working (incl. planning)
  Blocked = "blocked",        // stuck — needs a human or another task to unblock it
  Done = "done",
}

// A plan an assigned agent wrote for a task, kept under .agents/plans/ in its repo.
export interface TaskPlan {
  agentId: string;
  agentName: string;
  path: string;        // ".agents/plans/{task-id}-{slug}.md" (repo-relative)
  summary?: string;
  content?: string;    // full plan markdown, captured at registration (optional)
  createdAt: string;
}

// A live progress/output line an agent streams onto a task as it works (via the
// CLI `task-progress` → POST /api/agent). Shown in the task's detail overlay;
// updates arrive over SSE as task_update broadcasts.
export interface TaskProgress {
  agentId: string;
  agentName: string;
  content: string;
  at: string;
  // Output (default) = a line streamed by an agent; Instruction = a human
  // note added from the Web UI's task detail to steer the work.
  kind?: TaskProgressKind;
}

// How a TaskProgress line was produced (see TaskProgress.kind).
export enum TaskProgressKind {
  Output = "output",
  Instruction = "instruction",
}

export interface Task {
  id: string;
  title: string;
  description: string;          // the goal / details
  org: string | null;          // incode | metamap | aid
  createdBy: string;           // operator account/email, or "human"
  /** OIDC sub of the creator, when a signed-in human created it — the delivery
   *  target for the task-done notification (createdBy is only an email string). */
  createdBySub?: string | null;
  createdAt: string;
  updatedAt: string;
  status: TaskStatus;
  assignedAgentIds: string[];   // [] ⇒ unassigned → orchestrator picks up
  pickedUpBy: string[];         // agent ids that have acknowledged / started
  orchestratorId: string | null; // set when the orchestrator claims an unassigned task
  plans: TaskPlan[];            // plan refs written under .agents/plans
  progress: TaskProgress[];     // live output lines streamed by agents as they work
}

// The agent-facing view of a task an agent should act on (assigned to it, or —
// for the orchestrator — unassigned in its org). Mirrors AgentInput's role.
export interface TaskInput {
  taskId: string;
  title: string;
  description: string;
  org: string | null;
  status: TaskStatus;
  assignedAgentIds: string[];
  /** Agents that have already picked the task up. The reader checks whether IT is
   *  in here to decide if it still needs to claim — the shared `status` can't tell
   *  "I already claimed" from "another assignee claimed" once a multi-agent task
   *  has flipped to in_progress. */
  pickedUpBy: string[];
  /** true when this task has no assigned agents and the reader is the orchestrator. */
  needsAssignment: boolean;
  createdAt: string;
  updatedAt: string;
}

// Agent ingress protocol — the request/response messages exchanged between the
// hub's `/api/agent` ingress and omni-hub-cli. (Historically a WebSocket protocol;
// the same message shapes now travel over HTTP.)

// Activity an agent signals about a session, surfaced as live indicators in the
// Web UI. Working = generating a response; Idle = stopped (also sent
// automatically when the agent responds). Seen is derived by the hub when an
// agent reads inputs — not sent by the client.
export enum AgentActivityState {
  Seen = "seen",
  Working = "working",
  Idle = "idle",
}

export type HttpClientMessage =
  // Registration requires a valid org and an operator account (the server rejects
  // both missing) and binds the agent to the verified caller (see Agent.owner).
  | { type: "register"; agentName: string; account: string; org: Org; model?: string | null; repo?: string | null }
  | { type: "read_inputs"; agentId: string; since: string }
  | { type: "respond"; agentId: string; sessionId: string; content: string }
  | { type: "activity"; agentId: string; sessionId: string; state: AgentActivityState.Working | AgentActivityState.Idle }
  | { type: "suggest_summary"; agentId: string; sessionId: string; summary: string }
  | { type: "check_approval"; agentId: string; sessionId: string }
  // ── Tasks ──
  // List tasks for this agent; with onlyUnassigned, the orchestrator's pool view.
  | { type: "read_tasks"; agentId: string; onlyUnassigned?: boolean }
  // Orchestrator attaches the agents needed to finish an unassigned task.
  | { type: "attach_task_agents"; agentId: string; taskId: string; agentIds: string[] }
  // An assigned agent acknowledges/starts a task (→ planning).
  | { type: "pickup_task"; agentId: string; taskId: string }
  // Register a plan written under .agents/plans and notify the operator (→ in_progress).
  | { type: "register_task_plan"; agentId: string; taskId: string; path: string; summary?: string; content?: string }
  // Update a task's status (e.g. in_progress | blocked | done).
  | { type: "set_task_status"; agentId: string; taskId: string; status: TaskStatus }
  // Stream a live progress/output line onto a task as the agent works.
  | { type: "task_progress"; agentId: string; taskId: string; content: string }
  // ── DevHub page assists ──
  // Claim an assist (→ running) / report its result or failure (→ done | error).
  // Addressed by assistId or by the carrier task's taskId (at least one required).
  | { type: "claim_assist"; agentId: string; assistId?: string; taskId?: string }
  | { type: "complete_assist"; agentId: string; assistId?: string; taskId?: string; result?: string; error?: string };

export type HttpServerMessage =
  | { type: "registered"; agentId: string; agentName: string; account: string | null; org: string | null; repo: string | null; model: string | null }
  | { type: "inputs_result"; inputs: AgentInput[] }
  | { type: "output_sent"; outputId: string }
  | { type: "activity_ack"; state: AgentActivityState.Working | AgentActivityState.Idle }
  | { type: "summary_proposed"; proposalId: string }
  | { type: "approval_status"; status: ApprovalStatus; proposalId: string | null }
  | { type: "tasks_result"; tasks: TaskInput[] }
  | { type: "task_ok"; task: Task }
  | { type: "assist_ok"; assist: DevhubAssist }
  // Any ingress failure: the handler answers { error } with a 4xx/5xx status.
  | { type: "error"; error: string };

// The ingress speaks plain HTTP: the message `type` above is the POST route
// (`/api/agent/<type-with-dashes>`), not a body field. These helpers project a
// union member to the actual request/response body, so the CLI can type what it
// sends/receives against the protocol and drift becomes a compile error.
export type HttpClientBody<T extends HttpClientMessage["type"]> = Omit<
  Extract<HttpClientMessage, { type: T }>,
  "type"
>;
export type HttpServerBody<T extends HttpServerMessage["type"]> = Omit<
  Extract<HttpServerMessage, { type: T }>,
  "type"
>;

// Live agent activity for a session (read receipts + working indicator).
export interface AgentActivity {
  sessionId: string;
  agentId: string;
  agentName: string;
  state: AgentActivityState;
  at: string; // when the activity was signaled
  seenAt?: string; // for "seen": timestamp of the latest message the agent has read
}

export interface AgentInput {
  outputId: string;
  sessionId: string;
  sessionName: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
  kind: OutputKind;
  isHumanNote: boolean;
  currentSummary: string;
  currentContext: string; // the session's current context document (shared input)
}


// ── Tenancy & auth identity records ───────────────────────────────────────────
// (DevHub CMS types live in devhub-types.ts, re-exported above.)
// An explicit user→org membership grant, keyed by OIDC sub. Unioned with the
// agent-ownership-derived orgs by the api's tenant-scope resolution
// (core/authz.ts allowedOrgsFor), so a web-only user — who registers no agents
// and would otherwise derive ZERO orgs — can be granted scope by an admin.
export interface OrgMembership {
  sub: string;
  email: string;
  name: string | null;
  /** orgs this user may read/act within (each a member of ORGS) */
  orgs: string[];
  updatedAt: string;
}

// A human who has signed in via Okta SSO, keyed by OIDC sub. Recorded best-effort
// on token verification (throttled) so an admin can see who has access and assign
// CMS roles from a list instead of hand-entering opaque subs. Distinct from
// devhub_roles (which grants access) — this only records that a sign-in happened.
export interface AuthUserRecord {
  sub: string;
  email: string;
  name: string | null;
  image: string | null;
  /** first time we saw this user sign in */
  firstSeenAt: string;
  /** most recent sign-in */
  lastLoginAt: string;
}


// ── Repo clones (on-demand container clones) ──────────────────────────────────
// A repository this container has cloned to its writable working area on demand
// (POST /api/repos/:org/:name/clone), using the GitHub CLI (gh repo clone).
// Distinct from the read-only mirror catalog (RepoEntry.cloned, baked into the
// image): these are real working clones on the container's REPOS_DIR, tracked
// here so the Web UI can show — and filter to — only what this container actually
// has on disk. Cloning runs in the background; status moves
// pending → cloning → cloned | error, each step broadcast over SSE.
export enum RepoCloneStatus {
  Pending = "pending",   // request accepted, not started yet
  Cloning = "cloning",   // gh repo clone / git pull running
  Cloned = "cloned",     // present on disk
  Error = "error",       // clone/pull failed (see `error`)
}

export interface RepoClone {
  /** "<org>/<name>", e.g. "incode/analytics-service" — the record id. */
  id: string;
  /** Local mirror group: incode | metamap. */
  org: string;
  name: string;
  /** Same as id ("<org>/<name>"). */
  fullName: string;
  /** GitHub owner the clone came from, e.g. "IncodeTechnologies". */
  owner: string;
  status: RepoCloneStatus;
  /** Absolute path of the clone on the container, once cloned (else null). */
  path: string | null;
  /** Branch checked out, once known. */
  branch: string | null;
  /** Short HEAD commit sha, once cloned/pulled. */
  commit: string | null;
  /** Failure message when status is "error" (else null). */
  error: string | null;
  /** Email of the user who requested the clone. */
  requestedBy: string;
  /** When the clone last completed successfully (ISO), or null. */
  clonedAt: string | null;
  updatedAt: string;
}

// ── Autonomous agent runtimes (the Agents Hub → Runtime page) ─────────────────
// One record per repo an autonomous agent has been started on. The agent itself
// runs as its own container; this row is the control-plane state the Runtime page
// renders and the launcher drives.
//
// `idle` and `stopped` are deliberately distinct and must never be collapsed:
// idle means the agent slept itself and WILL wake when work arrives for its repo,
// stopped means an operator turned it off and it will NOT. Merging them makes
// "why isn't my agent picking this up?" unanswerable.
export enum AgentRuntimeState {
  Stopped = "stopped",   // operator intent: off. Never auto-wakes.
  Starting = "starting", // launch requested, container not registered yet
  Running = "running",   // registered and polling
  Idle = "idle",         // slept itself after the idle window; wakes on new work
  Stopping = "stopping", // stop requested, container not gone yet
  Error = "error",       // launch or run failed (see `error`)
}

export interface AgentRuntime {
  /** "<org>/<repo>", e.g. "incode/omni-hub" — the record id. */
  id: string;
  org: string;
  repo: string;
  state: AgentRuntimeState;
  /**
   * Whether this agent may open pull requests on its repo. Read-only is the
   * default and writes are a per-repo opt-in whose real gate is a repo-scoped
   * credential resolving — never this flag alone.
   */
  writeEnabled: boolean;
  /**
   * Reasoning depth this repo's agent runs at. Applied as a LAUNCH override, so a
   * change takes effect on the next start — a running container keeps the effort it
   * booted with, and the UI says so rather than implying it took effect.
   */
  effort: AgentEffort;
  /** The container currently backing this runtime (ECS task ARN), or null. */
  taskArn: string | null;
  /**
   * The most recent task ARN, whatever the state — set at launch and NEVER
   * cleared. `taskArn` is deliberately dropped when a runtime stops, so this is
   * what keeps the log stream reachable afterwards: the logs of a run that just
   * failed are exactly the ones an operator wants, and they outlive the task.
   */
  lastTaskArn: string | null;
  /**
   * A NON-SECRET leading fragment of the agent's token, e.g. "oha_7f3c" — enough
   * to identify which credential an agent holds without the overview holding one.
   * The full value is returned exactly once, by start/reauth, and is never
   * recoverable: `agent_tokens` stores only a SHA-256.
   */
  tokenPrefix: string | null;
  tokenIssuedAt: string | null;
  /** The agent row this runtime registered as, once it has registered. */
  agentId: string | null;
  lastSeenAt: string | null;
  /** Email of the admin who started it. */
  startedBy: string | null;
  startedAt: string | null;
  /** When it last put itself to sleep (ISO), or null. */
  sleptAt: string | null;
  /** Failure message when state is "error" (else null). */
  error: string | null;
  /**
   * SHA-256 of the single-use launch ticket issued to the container this runtime
   * last launched — never the value. Cleared the moment the ticket is spent, which
   * is what makes it single-use: a replay finds no ticket to match.
   */
  launchTicketHash: string | null;
  /** ISO expiry of that ticket. Past it, the ticket is refused as expired. */
  launchTicketExpiresAt: string | null;
  /**
   * Today's spend, as the agent last reported it. Null until it has answered
   * something. Reset by the agent when the UTC day rolls over, not here — the
   * agent owns the window it counts over.
   */
  spend: AgentRuntimeSpend | null;
  updatedAt: string;
}

/**
 * Reasoning depths an agent can run at. `medium` is the default — this worker does
 * light work over a fixed tool set, and output is the expensive half of a token
 * bill — with `high` available per repo for work that genuinely needs the depth.
 *
 * Deliberately NOT the API's full set. `low` is too shallow to trust with a repo,
 * and `xhigh`/`max` are the settings that make a "light work" agent expensive
 * enough to deserve its own budget conversation; offering those from a toggle would
 * make that an accident rather than a decision.
 */
export const AGENT_EFFORTS = ["medium", "high"] as const;
export type AgentEffort = (typeof AGENT_EFFORTS)[number];
export const DEFAULT_AGENT_EFFORT: AgentEffort = "medium";

/** What one agent has spent today, reported by the agent itself. */
export interface AgentRuntimeSpend {
  /** UTC date (YYYY-MM-DD) this covers. */
  day: string;
  answers: number;
  inputTokens: number;
  outputTokens: number;
  /** Tokens served from the prompt cache — a tenth of input price. */
  cacheReadTokens: number;
  cacheWriteTokens: number;
  /** Model turns across all of today's answers. */
  turns: number;
  /** When the agent last reported (ISO). */
  reportedAt: string;
}

/**
 * One row of the Runtime page: an eligible repo joined with its runtime record.
 * Repos with no record yet are reported as `stopped` with a null runtime, so the
 * page renders the full eligible set rather than only what has been started.
 */
export interface AgentRuntimeEntry {
  org: string;
  repo: string;
  /** "<org>/<repo>". */
  fullName: string;
  /** First line of the repo's .agents/ spec, when it has one. */
  description: string | null;
  state: AgentRuntimeState;
  runtime: AgentRuntime | null;
  /**
   * Queued tasks assigned to this repo's agents. Surfaced on the row because a
   * waiting task on a STOPPED runtime is exactly the state where the system looks
   * broken and isn't — nothing will pick it up until someone starts the agent.
   */
  waitingTasks: number;
  /**
   * Whether writes CAN be enabled for this repo — i.e. whether a repo-scoped
   * credential resolves. The toggle is disabled without it, with `writesReason`
   * shown verbatim, so the UI never offers a capability the runtime lacks.
   */
  writesAvailable: boolean;
  /** Why writes are unavailable, naming the variable to set. Null when available. */
  writesReason: string | null;
}

// Knowledge-base content repo (omni-system-knowledge-base) cloned at runtime and
// served from SYSTEM_ROOT — content is NOT baked into the image. This status backs
// the sidebar "clone & map knowledge base" control and streams over SSE
// (knowledge_base_update) as a clone/refresh runs.
export interface KnowledgeBaseStatus {
  /** A content repo is known (KB_REPO env, or the built-in default). */
  configured: boolean;
  /** "owner/name" of the content repo, or null when none is known. */
  repo: string | null;
  /** Branch/ref tracked, as configured (null ⇒ the repo default). */
  ref: string | null;
  /** Directory the repo is cloned into. */
  dir: string;
  /** Path content is served from (SYSTEM_ROOT). */
  systemRoot: string;
  /** Content is present on disk (system/ exists) — i.e. mapped and serveable. */
  present: boolean;
  /** Lifecycle of the last/ongoing clone or refresh. */
  phase: "idle" | "cloning" | "ready" | "error";
  /** Actual checked-out branch, once known. */
  branch: string | null;
  /** Short HEAD commit sha, once known. */
  commit: string | null;
  /** Failure message when phase is "error" (else null). */
  error: string | null;
  /** When content last cloned/updated successfully (ISO), or null. */
  lastSyncedAt: string | null;
  /** Whether a clone can run here (gh + token + writable dir), and why not. */
  canClone: boolean;
  reason: string | null;
}

// ── Repo AI-native readiness (on-demand re-scan overlay) ──────────────────────
// A freshly-scanned AI-native readiness result for one repo, stored in Postgres
// (repo_readiness) as an overlay the /api/repos listing merges OVER the static
// system/common/github_repositories.md table. The static table is generated
// offline (system/common/repos/scan-readiness.py); this overlay lets an admin
// re-analyze repos live from the Repositories page ("Check for AI readiness")
// without waiting for that offline regeneration. Keyed by "<org>/<name>", where
// org is the local mirror group (incode | metamap). One document per repo; a scan
// upserts it and broadcasts repo_readiness_update.
export interface RepoReadiness {
  /** "<org>/<name>", e.g. "incode/analytics-service" — the record id. */
  id: string;
  /** Local mirror group: incode | metamap. */
  org: string;
  name: string;
  /** Same as id ("<org>/<name>"). */
  fullName: string;
  /** AI-native readiness label, e.g. "🟢 Optimized" or "⬜ Not ready". */
  readiness: string;
  /** Detected AI-native markers, e.g. ["CLAUDE.md", "AGENTS.md", ".agents/"]. */
  markers: string[];
  /** When this repo was last scanned (ISO). */
  scannedAt: string;
  /** Email of the admin who ran the scan that produced this result. */
  scannedBy: string;
}

// Progress of the org-wide "Check for AI readiness" scan (a single background run
// at a time). Streams over SSE (repo_readiness_scan) so the button can show live
// progress; the per-repo results arrive separately as repo_readiness_update.
export interface RepoReadinessScanStatus {
  phase: "idle" | "scanning" | "done" | "error";
  /** Repos analyzed so far in the current/last run. */
  scanned: number;
  /** Repos to analyze in the current run (0 until enumeration finishes). */
  total: number;
  /** When the current/last run started (ISO), or null. */
  startedAt: string | null;
  /** When the last run finished (ISO), or null while running / never run. */
  finishedAt: string | null;
  /** Email of the admin who started the current/last run, or null. */
  startedBy: string | null;
  /** Failure message when phase is "error" (else null). */
  error: string | null;
}

// ── Omni Hub notifications ─────────────────────────────────────────────────
// Per-user, in-app notifications surfaced by the top-bar bell. One generic
// document per (recipient, event), delivered STRICTLY to its recipient (each
// person only ever reads their own — the store filters on recipientSub). Fired
// from several sources across the app; each carries an in-app `href` so clicking
// it lands the user on the thing that happened. IN-APP ONLY, no email.
export type NotificationKind =
  | "devhub_review" // a DevHub draft-review workflow transition
  | "session_shared" // you were tagged into a session
  | "session_activity" // new agent output / human note in a session you're in
  | "task_done" // a task you created reached "done"
  | "devhub_sdk_release" // an SDK repo cut a release branch the CMS had not seen
  | "devhub_sdk_review_pr" // the SDK team closed a review PR we had sent them
  | "devhub_publish_late"; // a merged publish has not reached the site within the target

export interface HubNotification {
  id: string;
  /** OIDC sub of the recipient — the delivery filter (each person reads only their own). */
  recipientSub: string;
  kind: NotificationKind;
  /** primary line — the subject (page/session/task title). */
  title: string;
  /** secondary line — what happened (review label, "New activity", …). */
  body?: string | null;
  /** in-app deep link — the click target (e.g. "/hub/sessions?s=<id>"). */
  href: string;
  /** the resource id this concerns (sessionId/taskId/…); used to coalesce noisy sources. */
  ref?: string | null;
  /** email of the actor who triggered it, when there is one. */
  fromUser?: string | null;
  createdAt: string;
  /** ISO time the recipient marked it read; null/absent while unread. */
  readAt?: string | null;
  /**
   * ISO time the recipient archived it; null/absent while it is in Unread or
   * Read (ENG-56868).
   *
   * Archiving DOES NOT touch `readAt`. That is what makes "restoring returns the
   * notification to the tab it came from, with its read state intact" true by
   * construction rather than by a second field remembering where it was — the
   * bucket a row belongs to is derived from the two timestamps, so there is no
   * remembered state to get wrong.
   */
  archivedAt?: string | null;
}

// SSE events pushed to Web UI
// ── Guides (internal, per org) ──────────────────────────────────────────────
// Internal guide collections under content/<org>/internal/<collection>/ in the
// DevHub corpus repo — NOT a docs version, so neither the CMS nor the public site
// reads them. Served and edited only by Omni Hub ▸ Guides (/api/guides).

export interface GuidePage {
  /** "<collection>/<slug>" — unique within the org. */
  path: string;
  slug: string;
  title: string;
  /** Menu group (page.yml `group`), null for top-level pages. */
  group: string | null;
  /** The collection's landing page (page.yml `landing: true` at the top level). */
  landing: boolean;
}

export interface GuideCollection {
  id: string;
  title: string;
  description: string | null;
  /** Slug of the page a new page starts from by default (`_collection.yml`
   *  `template:`), or null. Always one of `pages`. */
  template: string | null;
  /** Pages in menu order (the collection's and each group's `_order.yaml`). */
  pages: GuidePage[];
}

export interface GuideList {
  org: string;
  collections: GuideCollection[];
  /** The caller may save edits (the org's DevHub `drafts:publish` permission). */
  canEdit: boolean;
}

export interface GuidePageContent extends GuidePage {
  collection: string;
  markdown: string;
  /** sha256 of `markdown` — sent back on save so a stale edit is refused (409). */
  hash: string;
  /** Corpus-repo path of the page's current.md (for the "view source" link). */
  repoPath: string;
  /** Where the code disagrees with this page (its generated `checks.json`), for the
   *  right-hand panel. Empty when the page has none. */
  checks: GuideCheck[];
  /** In-guide link to the page's section of the full Tickets & Code Map, if any. */
  checksMap: string | null;
}

export interface GuideCheck {
  /** The guide section it belongs to, e.g. "§3.2 Module status". */
  section: string;
  guideSays: string;
  codeSays: string;
  repo: string;
  /** File (and line range) the code says it in, e.g. "values.yaml:413-433". */
  file: string;
  /** "master" or the unmerged branch the code is on. */
  ref: string;
  /** Commit-pinned Bitbucket link to those lines. */
  url: string;
}

export interface GuideCreateInput {
  title: string;
  /** An existing menu group's title, or null for a top-level page. */
  group: string | null;
  /** Slug of a page in the same collection to copy the body of, or null for blank. */
  from: string | null;
}

export interface GuideCreateResult extends GuideSaveResult {
  slug: string;
}

export interface GuideSaveResult {
  prUrl: string;
  merged: boolean;
  /** The saved text is already what Omni Hub reads (the clone caught up). */
  landed: boolean;
}

export type SseEvent =
  | { event: "agent_update"; data: Agent }
  | { event: "session_update"; data: Session }
  | { event: "session_deleted"; data: { id: string } }
  | { event: "new_output"; data: Output & { sessionId: string } }
  | { event: "proposal_update"; data: SummaryProposal }
  | { event: "presence_update"; data: HumanPresence }
  | { event: "agent_activity"; data: AgentActivity }
  | { event: "task_update"; data: Task }
  | { event: "task_deleted"; data: { id: string } }
  | { event: "devhub_draft_update"; data: DevhubDraft }
  | { event: "devhub_draft_deleted"; data: { id: string; pagePath?: string } }
  // A page left the corpus, or came back (ENG-55775, QA). The menu is built from
  // a corpus scan that only runs on load, so without this a deleted page sat in
  // every open sidebar — including the deleting writer's — until a refresh, and a
  // restored one stayed missing. Carries the served path only, like
  // `devhub_draft_deleted`: every listener already holds the entry it names.
  | { event: "devhub_page_deleted"; data: { pagePath: string } }
  | { event: "devhub_page_restored"; data: { pagePath: string } }
  | { event: "devhub_mapping_update"; data: DevhubSectionOwner }
  | { event: "devhub_order_update"; data: DevhubOrder }
  // An SDK's repo mapping, staged release, or published release changed. Carries
  // the whole summary rather than a delta: the SDK pane renders one row per SDK,
  // and a partial event would need the client to merge state it may not hold yet.
  | { event: "devhub_sdk_update"; data: DevhubSdkSummary }
  | { event: "devhub_component_update"; data: { org: string; version: string; slug: string } }
  | { event: "devhub_component_deleted"; data: { org: string; version: string; slug: string } }
  // An inline variable was created, edited, published or removed. Carries the key
  // only, like the component events: a value change is interesting to every open
  // Variables tab, and each refetches the list (and its own usage scan) rather
  // than merging a partial row it may not hold yet.
  | { event: "devhub_variable_update"; data: { org: string; version: string; slug: string } }
  // A page property or a page's values changed (ENG-62136). `pagePath` when one page's.
  | { event: "devhub_properties_update"; data: { org: string; pagePath?: string } }
  // The AI guidance block — one per org+version, so no slug to carry. Published
  // and saved from the AI context tab; the pane re-reads on it so a block another
  // writer publishes arrives without a reload.
  | { event: "devhub_ai_guidance_update"; data: { org: string; version: string } }
  // The authored Ask-AI system prompt + retrieval settings. A separate event from
  // the guidance block's: they publish separately, to different files, behind
  // different gates, and each pane re-reads only its own.
  | { event: "devhub_ai_settings_update"; data: { org: string; version: string } }
  | { event: "devhub_variable_deleted"; data: { org: string; version: string; slug: string } }
  | { event: "devhub_asset_update"; data: DevhubAssetSummary }
  | { event: "devhub_asset_deleted"; data: { id: string; org: string } }
  | { event: "devhub_redirect_update"; data: DevhubRedirect }
  | { event: "devhub_redirects_bulk_update"; data: { org: string; rows: DevhubRedirect[] } }
  | { event: "devhub_redirect_settings_update"; data: DevhubRedirectSettings }
  | { event: "devhub_assist_update"; data: DevhubAssist }
  | { event: "devhub_feedback_update"; data: DevhubFeedback }
  // A page tag was created, renamed, or had its page list change. Carries the
  // whole tag (including `pagePaths`) rather than a delta: the Tags pane renders
  // one row per tag with its page count, and membership is edited from both the
  // tag's page list and a page's own tag control, so a delta would need the
  // client to merge state the other screen may have moved underneath it.
  //
  // Tagged by ORG, like every other devhub event — so it reaches same-org clients
  // whose role could not read GET /tags (tag management is tech-writer/admin).
  // That is acceptable here where it would not be for a draft: a tag carries a
  // taxonomy label and page paths, both of which name published pages the same
  // clients can already list, and no document content. See draftForBroadcast for
  // the case where content DID have to be stripped.
  | { event: "devhub_tag_update"; data: DevhubTag }
  | { event: "devhub_tag_deleted"; data: { id: string; org: string } }
  // Teams (ENG-54991). Carries the id rather than the row: a team names its
  // members, and this broadcast is tagged only by org — so it reaches every
  // same-org client, including the SMEs and readers the Settings gate keeps out
  // of the roster. Clients refetch through the gated route, exactly as
  // draftForBroadcast blanks a draft's body for the same reason.
  | { event: "devhub_team_update"; data: { id: string; org: string } }
  | { event: "devhub_team_deleted"; data: { id: string; org: string } }
  // Variable categories (ENG-55728) — id only, like the tag events: the pane
  // refetches the (ungated) list rather than trusting a broadcast tagged by org.
  | { event: "devhub_variable_category_update"; data: { id: string; org: string; version: string } }
  | { event: "devhub_variable_category_deleted"; data: { id: string; org: string; version: string } }
  // A release batch changed — created, renamed, membership moved, approved,
  // published or archived. Org-tagged, and the whole row: every surface that
  // shows a batch (the card list, the detail view, a page's own label) refetches
  // from it, and membership is editable from more than one of them.
  | { event: "devhub_batch_update"; data: DevhubBatch }
  | { event: "repo_clone_update"; data: RepoClone }
  // A runtime row changed without anyone clicking — the reconciler correcting a
  // row whose container died, or stopping an orphan.
  | { event: "agent_runtime_update"; data: AgentRuntime }
  | { event: "repo_clone_deleted"; data: { id: string } }
  | { event: "repo_readiness_update"; data: RepoReadiness }
  | { event: "repo_readiness_scan"; data: RepoReadinessScanStatus }
  | { event: "knowledge_base_update"; data: KnowledgeBaseStatus }
  // Contentless global ping: "your notifications may have changed". Carries no
  // content (leak-free); each bell refetches its own self-scoped list on it.
  | { event: "notification_ping"; data: Record<string, never> };
